/**
 * Update-path telemetry.
 *
 * WHY THIS EXISTS
 *
 * The whole update path carried NO telemetry: a grep of this module for Sentry,
 * trackEvent, captureError or addBreadcrumb returned nothing (2026-09-20 audit).
 * So for any user on an old build we could not tell whether the banner was ever
 * shown, whether they dismissed it, or whether they tried to update and it
 * failed. Every explanation for a stale client was unfalsifiable.
 *
 * BREADCRUMBS, NOT EVENTS — and the limit is stated on purpose.
 * A breadcrumb costs nothing and is attached to whatever the user reports next,
 * which is exactly what triage needs ("this person was told, four times"). It
 * does NOT give a population count: a breadcrumb is only transmitted alongside
 * an error or a replay, so a healthy user who never crashes emits nothing here.
 * The population answer comes from Sentry release health (session tracking,
 * enabled in each app's Sentry.init) — the two are complementary and neither
 * substitutes for the other.
 *
 * NO SENTRY IMPORT HERE, DELIBERATELY. This module is compiled into all four
 * apps (the three Vite PWAs reach it through their `@shared` alias), and their
 * tsc programs do not all resolve `@sentry/react` — sales-client's failed CI on
 * exactly that, with `TS2307: Cannot find module '@sentry/react'`, while the
 * other two happened to pass. Same reason UpdateAvailableBanner hand-rolls its
 * button classes rather than importing the shared <Button>: shared chrome stays
 * portable, and each app wires its own sink at its entry point.
 *
 * NO PII. Build identity and timestamps only: shas, `builtAt` values and the app
 * name, all of which are public in `/version.json`. Never a user, route, ticket
 * or form value.
 */

import type { VersionInfo } from './types';

/** Sentry breadcrumb category for every transition on the update path. */
export const UPDATE_BREADCRUMB_CATEGORY = 'app-version';

/**
 * The four transitions that, between them, answer "why is this client stale?":
 * were they told, did they wave it away, did they accept, did it work.
 */
export type UpdateTelemetryEvent =
  | 'update-detected'
  | 'banner-shown'
  | 'banner-dismissed'
  | 'reload-accepted';

/** Build identity only — see the NO PII note above. */
export interface UpdateBreadcrumbData {
  app: string;
  fromSha: string;
  fromBuiltAt: string;
  toSha: string;
  toBuiltAt: string;
}

/**
 * Structurally compatible with Sentry's `Breadcrumb`, without depending on the
 * package. An app's entry point passes this straight to `Sentry.addBreadcrumb`.
 */
export interface UpdateBreadcrumb {
  category: typeof UPDATE_BREADCRUMB_CATEGORY;
  type: 'info';
  level: 'info';
  message: UpdateTelemetryEvent;
  data: UpdateBreadcrumbData;
}

/** Sentry breadcrumb category for the service-worker takeover path (#4029). */
export const SW_UPDATE_BREADCRUMB_CATEGORY = 'sw-update';

/**
 * A new service worker took control of the page (`controllerchange`, or
 * vite-plugin-pwa's `onNeedReload`) while a form-fill guard held the reload
 * back. The takeover itself cannot be undone once the browser fires
 * `controllerchange` — this records the moment it happened WITHOUT an
 * immediate reload, so a client stuck running old JS under a new controller
 * (until the guard releases) is diagnosable from a later crash report rather
 * than reading as an unexplained stale build.
 */
export type SwUpdateTelemetryEvent = 'sw.takeover_without_reload';

export interface SwUpdateBreadcrumbData {
  app: string;
}

export interface SwUpdateBreadcrumb {
  category: typeof SW_UPDATE_BREADCRUMB_CATEGORY;
  type: 'info';
  level: 'warning';
  message: SwUpdateTelemetryEvent;
  data: SwUpdateBreadcrumbData;
}

/** Where recorded transitions go. Null until an app installs one. */
export type UpdateTelemetrySink = (crumb: UpdateBreadcrumb | SwUpdateBreadcrumb) => void;

let sink: UpdateTelemetrySink | null = null;

/**
 * Install the app's telemetry sink. Called once from each app's entry point,
 * right after Sentry.init, as `setUpdateTelemetrySink(Sentry.addBreadcrumb)`.
 * Passing null removes it (tests).
 *
 * Default is NO SINK: an app that never calls this records nothing rather than
 * failing, which is the correct behaviour for a build with no DSN configured.
 */
export function setUpdateTelemetrySink(next: UpdateTelemetrySink | null): void {
  sink = next;
}

function buildData(current: VersionInfo, latest: VersionInfo): UpdateBreadcrumbData {
  return {
    app: current.app,
    fromSha: current.sha,
    fromBuiltAt: current.builtAt,
    toSha: latest.sha,
    toBuiltAt: latest.builtAt,
  };
}

/**
 * Record one update-path transition.
 *
 * Never throws and never blocks: telemetry that can break the update path is
 * worse than no telemetry. A missing sink is a silent no-op.
 */
export function recordUpdateEvent(
  event: UpdateTelemetryEvent,
  current: VersionInfo,
  latest: VersionInfo,
): void {
  if (!sink) return;
  try {
    sink({
      category: UPDATE_BREADCRUMB_CATEGORY,
      type: 'info',
      level: 'info',
      message: event,
      data: buildData(current, latest),
    });
  } catch {
    /* telemetry must never break the update path */
  }
}

/**
 * Record a service-worker takeover that did NOT immediately reload (a form
 * fill was open — see `SwUpdateTelemetryEvent`). Never throws, never blocks.
 */
export function recordSwTakeoverWithoutReload(app: string): void {
  if (!sink) return;
  try {
    sink({
      category: SW_UPDATE_BREADCRUMB_CATEGORY,
      type: 'info',
      level: 'warning',
      message: 'sw.takeover_without_reload',
      data: { app },
    });
  } catch {
    /* telemetry must never break the update path */
  }
}
